import { SearchType } from '~/redux/api/evisearch/searchTypes';
import { DropdownFieldValue } from '~/types/Field';

export interface SuggestionsParams {
  filterId: string;
  type: SearchType;
  query: string;
  /**
   * Pilot field pk, when it cannot be derived from `filterId` — Unified Fields
   * filter ids are UUIDs that carry no pk.
   */
  pilotFieldId?: string;
}

export interface SuggestionsResponse {
  selectAllCount: string;
  values: DropdownFieldValue[];
}

export interface SuggestionSource extends SuggestionsResponse {
  /**
   * Higher priority sources appear first in merged output and win dedup conflicts.
   */
  priority: number;
  /**
   * When true, this source returns its full set of values (no pagination).
   * Its total count equals its values.length.
   *
   * When false/omitted, the source is paginated — its selectAllCount may exceed
   * the number of returned values and is used as the authoritative total.
   */
  returnsFullSet?: boolean;
}

/**
 * Merges suggestion results from multiple sources with priority-based deduplication.
 *
 * - Values from higher-priority sources appear first and are kept on conflict.
 * - Sources marked `returnsFullSet: true` return their full set (e.g., presets, UF options).
 * - Paginated sources (not marked as full set) provide the authoritative total count.
 *   Their selectAllCount values are summed, then unique values from complete sources
 *   that aren't present in any paginated source are added to the total.
 * - If all sources are complete, selectAllCount equals the deduplicated values length.
 * - Preserves the "+" suffix to indicate exceeds-count.
 */
export const mergeSuggestionSources = (
  sources: SuggestionSource[],
): SuggestionsResponse => {
  if (sources.length === 0) {
    return { values: [], selectAllCount: '0' };
  }

  if (sources.length === 1) {
    return {
      values: sources[0].values,
      selectAllCount: sources[0].selectAllCount,
    };
  }

  const sorted = [...sources].sort((a, b) => b.priority - a.priority);
  const mergedValues = deduplicateValues(sorted);
  const selectAllCount = computeSelectAllCount(sorted);

  return {
    values: mergedValues,
    selectAllCount: selectAllCount || String(mergedValues.length),
  };
};

function getKey(v: DropdownFieldValue): string {
  return String(v.value);
}

function deduplicateValues(sources: SuggestionSource[]): DropdownFieldValue[] {
  const seen = new Set<string>();
  const merged: DropdownFieldValue[] = [];
  for (const source of sources) {
    for (const v of source.values) {
      const key = getKey(v);
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(v);
      }
    }
  }
  return merged;
}

function computeSelectAllCount(sorted: SuggestionSource[]): string {
  const paginatedSources = sorted.filter((s) => !s.returnsFullSet);

  if (paginatedSources.length === 0) {
    return '';
  }

  const paginatedValueSet = new Set<string>();
  let paginatedTotal = 0;
  let exceedsCount = false;

  for (const source of paginatedSources) {
    for (const v of source.values) {
      paginatedValueSet.add(getKey(v));
    }
    const raw = source.selectAllCount;
    if (raw.endsWith('+')) exceedsCount = true;
    paginatedTotal += Number.parseInt(raw, 10) || 0;
  }

  const additionalValues = new Set<string>();
  for (const source of sorted.filter((s) => s.returnsFullSet)) {
    for (const v of source.values) {
      if (!paginatedValueSet.has(getKey(v))) {
        additionalValues.add(getKey(v));
      }
    }
  }

  return (
    String(paginatedTotal + additionalValues.size) + (exceedsCount ? '+' : '')
  );
}
